import { RouteConfig } from '../types/route';
import { ShieldAlert } from 'lucide-react';
import { Icon } from '@arcsite/icons';
import { AuthLayout } from '../layouts/AuthLayout';
import { PublicLayout } from '../layouts/PublicLayout';
import ChunkLazyLoadWrapper from '../components/LazyLoadWrapper/ChunkLazyLoadWrapper';
import { lazyWithFallback } from '../utils/lazy';
import { User } from '@/types/user';
import { permissions } from './permissions';
import { checkEmbedMode } from '@/utils/dom';

const TestPage = lazyWithFallback(() => import('../pages/Test'));

const DashboardHomePage = lazyWithFallback(
  () => import('../features/dashboard/pages/dashboard-home')
);

const LoginPage = lazyWithFallback(() => import('../pages/Auth/Login'));
const CompanyPage = lazyWithFallback(() => import('../pages/Companies'));
const CompanyDetailPage = lazyWithFallback(
  () => import('../pages/CompanyDetail')
);
const EmployeesPage = lazyWithFallback(() => import('../pages/Employees'));
const EmployeeDetailPage = lazyWithFallback(
  () => import('../pages/EmployeeDetail')
);

const PasswordResetListPage = lazyWithFallback(
  () => import('../pages/PasswordResetList')
);

const ProductLibrariesListPage = lazyWithFallback(
  () => import('../features/product-libraries/pages/product-libraries-list')
);
const EnterpriseLibraryPage = lazyWithFallback(
  () => import('../features/product-libraries/pages/enterprise-libraries')
);
const SettingsHomePage = lazyWithFallback(
  () => import('../features/settings/pages/home')
);

const CompanyConfigurationPage = lazyWithFallback(
  () => import('../features/configuration/page/configuration-detail')
);
const ProductDetail = lazyWithFallback(
  () =>
    import('../features/product-libraries/pages/product-detail/ProductDetail')
);
const BundleDetail = lazyWithFallback(
  () => import('../features/product-libraries/pages/bundle-detail/BundleDetail')
);

export const routes: RouteConfig[] = [
  {
    path: '/',
    element: <PublicLayout />,
    hideInMenu: true,
    children: [
      {
        path: 'login',
        element: <ChunkLazyLoadWrapper Component={LoginPage} />,
        meta: {
          title: 'Login',
        },
      },
    ],
  },
  {
    path: '/password-reset-list',
    element: <ChunkLazyLoadWrapper Component={PasswordResetListPage} />,
    meta: {
      title: 'Password Reset List',
    },
    hideInMenu: true,
  },

  {
    path: '/admin',
    element: <AuthLayout />,
    children: [
      {
        path: 'dashboard',
        element: <ChunkLazyLoadWrapper Component={DashboardHomePage} />,
        meta: {
          title: 'Dashboard',
          icon: <Icon name="chart-line-up-24x24" />,
          permissionCheck: permissions.enterpriseAdmin,
        },
      },
      {
        path: 'companies',
        element: <ChunkLazyLoadWrapper Component={CompanyPage} />,
        meta: {
          title: 'Companies',
          icon: <Icon name="company-24x24" />,
          permissionCheck: permissions.enterpriseAdmin,
        },
      },
      {
        path: 'company/:org_id',
        element: (
          <ChunkLazyLoadWrapper
            chunkName="company"
            Component={CompanyDetailPage}
          />
        ),
        hideInMenu: true,
        meta: {
          title: 'Company Detail',
          permissionCheck: permissions.enterpriseAdmin,
        },
      },
      {
        path: 'employees',
        element: <ChunkLazyLoadWrapper Component={EmployeesPage} />,
        meta: {
          title: 'Employees',
          icon: <Icon name="users-24x24" />,
          permissionCheck: permissions.enterpriseAdmin,
        },
      },
      {
        path: 'employee/:employee_id',
        element: <ChunkLazyLoadWrapper Component={EmployeeDetailPage} />,
        hideInMenu: true,
        meta: {
          title: 'Employee Detail',
          permissionCheck: permissions.enterpriseAdmin,
        },
      },
      {
        path: 'product_libraries',
        element: <ChunkLazyLoadWrapper Component={ProductLibrariesListPage} />,
        meta: {
          title: 'Products',
          icon: <Icon name="product-tags-24x24" />,
          permissionCheck: (user: User) =>
            permissions.enterpriseAdmin(user) &&
            permissions.enterpriseMvp2Beta(user),
        },
      },
      {
        path: 'enterprise_libraries',
        hideInMenu: true,
        element: <ChunkLazyLoadWrapper Component={EnterpriseLibraryPage} />,
        meta: {
          title: 'Enterprise Libraries',
          permissionCheck: (user: User) =>
            permissions.enterpriseAdmin(user) &&
            permissions.enterpriseMvp2Beta(user),
        },
      },
      {
        path: 'settings',
        element: <ChunkLazyLoadWrapper Component={SettingsHomePage} />,
        meta: {
          title: 'Settings',
          icon: <Icon name="advanced-settings-24x24" />,
          permissionCheck: (user: User) =>
            permissions.enterpriseAdmin(user) &&
            permissions.enterpriseMvp2Beta(user),
        },
      },
      {
        path: 'configuration/:configuration_id',
        element: <ChunkLazyLoadWrapper Component={CompanyConfigurationPage} />,
        hideInMenu: true,
        meta: {
          title: 'Products',
          icon: <Icon name="company-24x24" />,
          permissionCheck: (user: User) =>
            permissions.manageSettings(user) &&
            permissions.enterpriseMvp2Beta(user) &&
            checkEmbedMode(),
          getDynamicPath: (user: User) => {
            if (user.org_product_library_config_id) {
              return `/admin/configuration/${user.org_product_library_config_id}`;
            }
            return null;
          },
        },
      },
      {
        path: 'product/:configuration_id/:product_id',
        element: <ChunkLazyLoadWrapper Component={ProductDetail} />,
        hideInMenu: true,
        meta: {
          title: 'Product Detail',
          icon: <Icon name="company-24x24" />,
          permissionCheck: (user: User) =>
            permissions.manageSettings(user) &&
            permissions.enterpriseMvp2Beta(user) &&
            checkEmbedMode(),
        },
      },
      {
        path: 'bundle/:configuration_id/:bundle_id',
        element: <ChunkLazyLoadWrapper Component={BundleDetail} />,
        hideInMenu: true,
        meta: {
          title: 'Bundle Detail',
          icon: <Icon name="company-24x24" />,
          permissionCheck: (user: User) =>
            permissions.manageSettings(user) &&
            permissions.enterpriseMvp2Beta(user) &&
            checkEmbedMode(),
        },
      },
      {
        path: 'test',
        element: <TestPage />,
        hideInMenu: true,
        meta: {
          title: 'Test Page',
          icon: <ShieldAlert className="w-5 h-5" />,
        },
      },
    ],
  },
];
